import { createAsyncThunk } from '@reduxjs/toolkit';
import { PrebuiltTemplate, PrebuiltTemplateApiResponse } from './types';
import { getAuthContext } from '../../utils/jwt';
import { BASE_URL } from '../../utils/constants';

// Define the API response structure
interface ApiResponse {
  content: PrebuiltTemplateApiResponse[];
  last: boolean;
  page: number;
  size: number;
  totalCount: number;
  totalPages: number;
}


// Define the parameters for fetching prebuilt templates
export interface FetchPrebuiltTemplatesParams {
  orgId: string;
  page?: number;
  size?: number;
  name?: string;
  category?: string;
}

// Define the return type with pagination metadata
export interface FetchPrebuiltTemplatesResult {
  content: PrebuiltTemplate[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
}

export const fetchPrebuiltTemplates = createAsyncThunk<
  FetchPrebuiltTemplatesResult,
  FetchPrebuiltTemplatesParams,
  { rejectValue: string }
>('templates/fetchPrebuiltTemplates', async ({ orgId, page, size, name = '', category = '' }, { rejectWithValue }) => {
  try {
    const { token, tenantId, userId } = getAuthContext();

    if (!token) {
      return rejectWithValue('Authentication token not available');
    }

    let url = `${BASE_URL}/v1/templates/pre-build?orgId=${orgId}`;

    if (page !== undefined) {
      url += `&page=${page}`;
    }
    if (size !== undefined) {
      url += `&size=${size}`;
    }
    if (name && name.trim()) {
      url += `&name=${encodeURIComponent(name.trim())}`;
    }
    if (category && category.trim()) {
      url += `&metadata.category=${encodeURIComponent(category.trim())}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Tenant-Id': tenantId || '',
        'X-User-Id': userId || '',
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      // console.error('API Error:', response.status, errorText);
      return rejectWithValue(`Failed to fetch templates: ${response.status}`);
    }

    const apiResponse: ApiResponse = await response.json();
    const data = apiResponse.content;

    const templates: PrebuiltTemplate[] = data
      .map((item): PrebuiltTemplate | null => {
        let html = '';
        let css = item.styles?.trim() || '';

        if (item.blocks && Array.isArray(item.blocks)) {
          const rawBlock = item.blocks.find((b) => b.type === 'rawHtml');
          if (rawBlock?.text?.trim()) {
            html = rawBlock.text
              .trim()
              .replace(/\\n/g, '\n')
              .replace(/\\"/g, '"');
          }
        }

        if (!html && item.html?.trim()) {
          html = item.html.trim().replace(/\\n/g, '\n').replace(/\\"/g, '"');
        }

        if (!html) {
          console.warn('No HTML found for template:', item.id, item.name);
          return null;
        }

        const templateId = item.id || item._id;
        if (!templateId) {
          // console.error('No ID found for template:', item);
          return null;
        }

        return {
          id: templateId,
          name: item.name,
          description: item.description,
          html,
          css: css.replace(/\\n/g, '\n').replace(/\\"/g, '"'),
          createdAt: item.createdAt?.$date || new Date().toISOString(),
          category: item.metadata?.category || 'Uncategorized',
          isPrebuilt: true,
          thumbnail: null,
        };
      })
      .filter((t): t is PrebuiltTemplate => t !== null);

    return {
      content: templates,
      totalCount: apiResponse.totalCount || templates.length,
      totalPages: apiResponse.totalPages || Math.ceil(templates.length / (size || 9)),
      currentPage: apiResponse.page || page || 0,
      pageSize: apiResponse.size || size || 9,
    };
  } catch (err: any) {
    // console.error('Network error:', err);
    return rejectWithValue(err.message || 'Network error');
  }
});

export default fetchPrebuiltTemplates;